import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    savingMessage: String,
    validationMessage: String
  }

  static targets = ['errorFlash', 'infoFlash']

  connect() {
    this.pendingPublishResume = null

    this.boundHandleFetchRequest = this.handleFetchRequest.bind(this)
    this.boundTryPendingPublish = this.tryPendingPublish.bind(this)
    document.addEventListener('turbo:before-fetch-request', this.boundHandleFetchRequest)
    document.addEventListener('autosave:success', this.boundTryPendingPublish)
  }

  disconnect() {
    document.removeEventListener('turbo:before-fetch-request', this.boundHandleFetchRequest)
    document.removeEventListener('autosave:success', this.boundTryPendingPublish)
    this.pendingPublishResume = null
  }

  handleFetchRequest(event) {
    const url = event.detail.url?.href || event.detail.url
    if (!url?.includes('/publish')) return

    const pendingReason = this.getPendingReason()
    if (pendingReason) {
      event.preventDefault()
      this.showWarning(pendingReason)

      if (pendingReason === 'saving') {
        this.pendingPublishResume = event.detail.resume
        this.flushAutosaves()
      }
    }
  }

  tryPendingPublish() {
    if (typeof this.pendingPublishResume !== 'function') return

    const pendingReason = this.getPendingReason()
    if (pendingReason === 'saving') return

    if (pendingReason === 'validation') {
      this.showWarning('validation')
      this.pendingPublishResume = null
      return
    }

    const resume = this.pendingPublishResume
    this.pendingPublishResume = null
    resume()
  }

  flushAutosaves() {
    const flushEvent = new CustomEvent('autosave:flush', { bubbles: true })
    document.dispatchEvent(flushEvent)
  }

  getPendingReason() {
    // Check for forms currently submitting or fields with unsaved changes
    const isSaving = document.querySelector('form[data-submitting="true"]') ||
                     document.querySelector('[data-changed="true"]')
    if (isSaving) return 'saving'

    if (this.hasAnyValidationErrors()) return 'validation'

    return null
  }

  hasAnyValidationErrors() {
    const forms = document.querySelectorAll('form[data-controller*="auto-submit"]')

    for (const form of forms) {
      if (this.hasValidationErrors(form)) {
        return true
      }
    }
    return false
  }

  // Check if field is account_id (optional for publishing)
  isAccountField(field) {
    return field.name?.includes('account_id')
  }

  // Check if error/wrapper element is related to account_id field
  isAccountError(element) {
    // Empty error messages should be ignored
    if (element.classList?.contains('error-message') && !element.textContent?.trim()) return true
    // Wrapper containing account_id
    if (element.querySelector?.('[name*="account_id"]')) return true
    // Element in fieldset with account_id
    const fieldset = element.closest?.('fieldset.bordered-fieldset')
    return fieldset?.querySelector('[name*="account_id"]') != null
  }

  hasValidationErrors(form) {
    // Check for HTML5 validation errors (excluding account_id - optional for publishing)
    const invalidFields = Array.from(form.querySelectorAll(':invalid'))
      .filter(field => !this.isAccountField(field))
    if (invalidFields.length > 0) return true

    // Check for error messages in the form
    const errorMessages = Array.from(
      form.querySelectorAll('.error-message, .application-form-builder.error-message')
    ).filter(el => !this.isAccountError(el))
    if (errorMessages.length > 0) return true

    // Check for Rails field_with_errors wrapper
    const fieldsWithErrors = Array.from(form.querySelectorAll('.field_with_errors'))
      .filter(wrapper => !this.isAccountError(wrapper))
    if (fieldsWithErrors.length > 0) return true

    return false
  }

  showWarning(reason) {
    if (reason === 'validation') {
      this.focusFirstInvalidField()
      if (this.hasValidationMessageValue) {
        this.showFlashMessage(this.validationMessageValue, 'error')
      }
    } else if (reason === 'saving') {
      if (this.hasSavingMessageValue) {
        this.showFlashMessage(this.savingMessageValue, 'info')
      }
    }
  }

  focusFirstInvalidField() {
    const field = this.findFirstInvalidField()
    if (!field) return

    field.scrollIntoView({ behavior: 'smooth', block: 'center' })

    setTimeout(() => {
      field.focus()

      // For Rails errors, set custom validity so reportValidity shows the message
      if (field.validity?.valid) {
        const errorMessage = this.getRailsErrorMessage(field)
        if (errorMessage) {
          field.setCustomValidity(errorMessage)
          field.reportValidity()
          // Clear custom validity on next interaction
          const clear = () => field.setCustomValidity('')
          field.addEventListener('input', clear, { once: true })
        }
      } else {
        field.reportValidity()
      }
    }, 100)
  }

  findFirstInvalidField() {
    const forms = document.querySelectorAll('form[data-controller*="auto-submit"]')

    for (const form of forms) {
      // HTML5 invalid fields
      const html5Invalid = form.querySelector(':invalid:not([type="hidden"]):not(:disabled)')
      if (html5Invalid && !this.isAccountField(html5Invalid)) {
        return html5Invalid
      }

      // Rails validation error fields (inside .field_with_errors wrapper)
      const railsField = form.querySelector('.field_with_errors input, .field_with_errors select, .field_with_errors textarea')
      if (railsField && !this.isAccountField(railsField)) {
        return railsField
      }

      // Fields associated with error messages (not wrapped in .field_with_errors)
      const errorMessages = form.querySelectorAll('.error-message, .application-form-builder.error-message')
      for (const errorEl of errorMessages) {
        if (this.isAccountError(errorEl)) continue

        const field = errorEl.closest('.field')?.querySelector('input, select, textarea') ||
                     errorEl.previousElementSibling ||
                     errorEl.parentElement?.querySelector('input, select, textarea')

        if (field && !this.isAccountField(field)) {
          return field
        }
      }
    }

    return null
  }

  getRailsErrorMessage(field) {
    return field.closest('.field_with_errors')?.querySelector('.error-message')?.textContent?.trim() ||
           field.closest('.field')?.querySelector('.error-message, .application-form-builder.error-message')?.textContent?.trim()
  }

  showFlashMessage(message, type = 'error') {
    const template = this.flashTemplateFor(type)
    if (!template) return

    const flashesContainer = document.getElementById('flashes')
    if (!flashesContainer) return

    const flashElement = document.createElement('div')
    flashElement.innerHTML = template.innerHTML

    const messageElement = flashElement.querySelector('p')
    if (messageElement) {
      messageElement.textContent = message
    }

    flashesContainer.appendChild(flashElement.firstElementChild)
  }

  flashTemplateFor(type) {
    if (type === 'info' && this.hasInfoFlashTarget) return this.infoFlashTarget
    if (this.hasErrorFlashTarget) return this.errorFlashTarget

    return null
  }
}
